/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { Divider } from "./..";
import PropTypes from 'prop-types';
import { forwardRef, memo, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { bgColor, borderRadius4px, cursorNotAllowed, displayBlock, parseHeight, parseWidth, positionRelative, shadowNone } from "../../styles/general";
import { classNames } from "../../utils";
import AccordionContext from "./context";
const handleTransition = (timer, el, beginHeight, endHeight = null) => {
  const getAutoHeightDuration = height => {
    if (!height) {
      return 0;
    }
    const constant = height / 36;

    // https://www.wolframalpha.com/input/?i=(4+%2B+15+*+(x+%2F+36+)+**+0.25+%2B+(x+%2F+36)+%2F+5)+*+10
    return Math.round((4 + 15 * constant ** 0.25 + constant / 5) * 10);
  };
  const duration = getAutoHeightDuration(beginHeight);
  el.style.transitionDuration = `${duration}ms`;
  el.style.height = `${beginHeight}px`;
  if (timer.current) {
    clearTimeout(timer.current);
  }
  timer.current = setTimeout(() => {
    el.style.height = endHeight;
  }, duration);
};
const Accordion = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  action = {},
  boxShadow,
  children,
  className,
  disabled,
  expand,
  expanded,
  fullHeight,
  id,
  onClick,
  onCollapse,
  onExpand,
  style
}, reference) => {
  if (!reference) reference = useRef(null);
  const ref = useRef(null);
  const timer = useRef(null);
  const [expandState, setExpandState] = useState(expanded || expand);
  const _AccordionRootCSS = AccordionRootCSS(fullHeight, expandState);
  useEffect(() => {
    if (ref.current) {
      if (fullHeight) {
        handleTransition(timer, ref.current, ref.current.clientHeight, expandState ? '100%' : null);
      }
      // Handle margin top bot when in group
      const previous = ref.current.previousElementSibling;
      if (previous) {
        expandState ? previous.classList.add('previousExpanding') : previous.classList.remove('previousExpanding');
      }
    }
  }, [expandState]);
  useEffect(() => {
    expanded !== undefined ? setExpandState(expanded) : setExpandState(expand);
  }, [expand, expanded]);
  const onClickAccordion = () => {
    !!onClick && onClick({
      target: ref.current
    });
  };
  useImperativeHandle(reference, () => {
    const currentRef = ref.current || {};
    currentRef.element = ref.current;
    const _instance = {
      ...action
    }; // methods
    _instance.__proto__ = {}; // hidden methods
    currentRef.instance = _instance;
    return currentRef;
  });
  return useMemo(() => {
    var _ref$current;
    return jsx("div", {
      id: id,
      css: [_AccordionRootCSS, boxShadow && AccordionRootBoxShadowCSS, disabled && AccordionRootDisabledCSS],
      ref: ref,
      style: style,
      className: classNames('DGN-UI-Accordion', disabled && 'disabled', expandState && 'expanding', className)
    }, jsx(AccordionContext.Provider, {
      value: {
        expanded,
        expandState,
        disabled,
        onClickAccordion: onClick ? onClickAccordion : null,
        onExpand,
        onCollapse,
        setExpandState,
        handleTransition,
        fullHeight: fullHeight ? ((_ref$current = ref.current) === null || _ref$current === void 0 ? void 0 : _ref$current.clientHeight) || 0 : 0
      }
    }, children, jsx(Divider, {
      color: 'line/category',
      style: {
        margin: 0,
        display: 'none'
      }
    })));
  }, [boxShadow, children, className, disabled, expand, expanded, fullHeight, id, onClick, onCollapse, onExpand, style, expandState]);
}));
const AccordionRootCSS = (fullHeight, expandState) => css`
    ${displayBlock};
    ${positionRelative};
    ${borderRadius4px};
    ${parseWidth('100%')};
    ${fullHeight && expandState && parseHeight('100%')};
    ${bgColor('system/white')};
    transition: margin 300ms ease, height 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
`;
const AccordionRootBoxShadowCSS = css`
    box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.25);
`;
const AccordionRootDisabledCSS = css`
    ${cursorNotAllowed};
    ${shadowNone};
`;
Accordion.defaultProps = {
  boxShadow: true,
  className: '',
  disabled: false,
  expand: false,
  fullHeight: false,
  style: {}
};
Accordion.propTypes = {
  /** The box-shadow of component. */
  boxShadow: PropTypes.bool,
  /** The content of the component (insist <AccordionSummary />, <AccordionDetails />). */
  children: PropTypes.node,
  /** Class for component. */
  className: PropTypes.string,
  /** If `true`, the component is disabled. */
  disabled: PropTypes.bool,
  /** If `true`, expand immediate after render (shouldn't use this). */
  expand: PropTypes.bool,
  /** Expand state of accordion (should use this). */
  expanded: PropTypes.bool,
  /** If `true`, Accordion height will 100% of it parent. */
  fullHeight: PropTypes.bool,
  /** Callback fired when Accordion is collapsed. */
  onCollapse: PropTypes.func,
  /** Callback fired when Accordion is expanded. */
  onExpand: PropTypes.func,
  /** Style inline of component. */
  style: PropTypes.object
};
export default Accordion;